import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";

import { isApiClientError } from "../api/errors";
import { shellBootstrapKey, shellBootstrapQuery } from "./bootstrap";
import { LabeledInput } from "./LabeledInput";
import type { ShellServices } from "./services";
import { Notice } from "./Notice";
import { authRateLimitMessage, useAuthRateLimit } from "./useAuthRateLimit";

const recoveryNotice =
  "Если адрес зарегистрирован, ссылка для восстановления уже отправлена.";

function errorMessage(error: unknown): string {
  if (isApiClientError(error) && error.status === 429)
    return authRateLimitMessage;
  if (isApiClientError(error) && error.status === 403)
    return "Учётная запись заблокирована или действие недоступно. Обратитесь к администратору.";
  if (isApiClientError(error) && error.status === 422)
    return "Проверьте заполнение полей и повторите попытку.";
  return "Не удалось выполнить действие. Повторите позже.";
}

function Frame({ title, children }: { title: string; children: ReactNode }) {
  return (
    <main className="login-page">
      <section className="login-brand">
        <p className="eyebrow eyebrow--light">Безопасность аккаунта</p>
        <h1>Алтайэкопарк ERP</h1>
        <p>Защищённый доступ к рабочему пространству сотрудников.</p>
      </section>
      <section className="login-panel" aria-labelledby="auth-title">
        <div>
          <h2 id="auth-title">{title}</h2>
          {children}
        </div>
      </section>
    </main>
  );
}

function readEmailVerificationParams(value: unknown): {
  id: string;
  hash: string;
} | null {
  if (
    typeof value === "object" &&
    value !== null &&
    "id" in value &&
    "hash" in value &&
    typeof value.id === "string" &&
    typeof value.hash === "string"
  ) {
    return { id: value.id, hash: value.hash };
  }
  return null;
}

export function ForgotPasswordPage({ services }: { services: ShellServices }) {
  const [email, setEmail] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: () => services.identity.forgotPassword(email.trim()),
    onSuccess: () => setNotice(recoveryNotice),
    onError: (error) =>
      setNotice(
        isApiClientError(error) &&
          error.status >= 400 &&
          error.status < 500 &&
          error.status !== 429
          ? recoveryNotice
          : errorMessage(error),
      ),
  });
  return (
    <Frame title="Восстановление пароля">
      <form
        className="login-form"
        onSubmit={(event) => {
          event.preventDefault();
          mutation.mutate();
        }}
      >
        <LabeledInput
          label="Рабочий email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={setEmail}
        />
        {notice && <Notice>{notice}</Notice>}
        <button
          className="button button--primary button--wide"
          disabled={mutation.isPending}
        >
          Отправить ссылку
        </button>
        <Link className="button button--text" to="/login">
          Вернуться ко входу
        </Link>
      </form>
    </Frame>
  );
}

export function ResetPasswordPage({ services }: { services: ShellServices }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: () =>
      services.identity.resetPassword({
        email: email.trim(),
        token,
        password,
        password_confirmation: confirmation,
      }),
    onSuccess: async () => {
      await navigate({ to: "/login" });
    },
    onError: (error) => setNotice(errorMessage(error)),
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (password !== confirmation) {
      setNotice("Пароли не совпадают.");
      return;
    }
    mutation.mutate();
  };
  return (
    <Frame title="Новый пароль">
      <form className="login-form" onSubmit={submit}>
        <LabeledInput
          label="Email"
          type="email"
          required
          value={email}
          onChange={setEmail}
        />
        <LabeledInput
          label="Токен из письма"
          required
          value={token}
          onChange={setToken}
        />
        <LabeledInput
          label="Новый пароль"
          minLength={8}
          type="password"
          required
          value={password}
          onChange={setPassword}
        />
        <LabeledInput
          label="Повторите пароль"
          minLength={8}
          type="password"
          required
          value={confirmation}
          onChange={setConfirmation}
        />
        {notice && <Notice>{notice}</Notice>}
        <button
          className="button button--primary button--wide"
          disabled={mutation.isPending}
        >
          Сохранить пароль
        </button>
      </form>
    </Frame>
  );
}

export function EmailVerificationPage({
  services,
}: {
  services: ShellServices;
}) {
  const params: unknown = useParams({ from: "/email/verify/$id/$hash" });
  const verification = readEmailVerificationParams(params);
  const started = useRef(false);
  const mutation = useMutation({
    mutationFn: () =>
      services.identity.verifyEmail(
        verification?.id ?? "",
        verification?.hash ?? "",
      ),
  });
  useEffect(() => {
    if (verification !== null && !started.current) {
      started.current = true;
      mutation.mutate();
    }
  }, [mutation, verification]);
  return (
    <Frame title="Подтверждение email">
      {verification === null && (
        <Notice>Ссылка подтверждения недействительна.</Notice>
      )}
      {mutation.isPending && <p>Подтверждаем адрес…</p>}
      {mutation.isSuccess && (
        <>
          <Notice role="status">
            Email подтверждён. Теперь можно продолжить работу.
          </Notice>
          <Link className="button button--primary button--wide" to="/login">
            Перейти ко входу
          </Link>
        </>
      )}
      {mutation.isError && (
        <>
          <Notice>Ссылка подтверждения недействительна или устарела.</Notice>
          <Link className="button button--primary button--wide" to="/login">
            Перейти ко входу
          </Link>
        </>
      )}
    </Frame>
  );
}

function ChangePasswordForm({
  services,
  onSuccess,
}: {
  services: ShellServices;
  onSuccess: (message: string) => void;
}) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const rateLimit = useAuthRateLimit();
  const mutation = useMutation({
    mutationFn: () =>
      services.identity.changePassword({
        current_password: currentPassword,
        password,
        password_confirmation: confirmation,
      }),
    onSuccess: () => {
      setCurrentPassword("");
      setPassword("");
      setConfirmation("");
      setNotice(null);
      onSuccess("Пароль изменён. Все остальные устройства и сессии завершены.");
    },
    onError: (error) => {
      rateLimit.handleError(error);
      setNotice(errorMessage(error));
    },
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (mutation.isPending || rateLimit.blocked) return;
    if (password !== confirmation) {
      setNotice("Новый пароль и его подтверждение не совпадают.");
      return;
    }
    mutation.mutate();
  };
  return (
    <form className="login-form auth-settings-form" onSubmit={submit}>
      <LabeledInput
        label="Текущий пароль"
        type="password"
        autoComplete="current-password"
        required
        value={currentPassword}
        onChange={setCurrentPassword}
      />
      <LabeledInput
        label="Новый пароль"
        type="password"
        autoComplete="new-password"
        minLength={8}
        required
        value={password}
        onChange={setPassword}
      />
      <LabeledInput
        label="Повторите новый пароль"
        type="password"
        autoComplete="new-password"
        minLength={8}
        required
        value={confirmation}
        onChange={setConfirmation}
      />
      {notice && <Notice>{notice}</Notice>}
      <button
        className="button button--secondary"
        disabled={mutation.isPending || rateLimit.blocked}
      >
        Сменить пароль
      </button>
    </form>
  );
}

export function SecuritySettingsPage({
  services,
}: {
  services: ShellServices;
}) {
  const client = useQueryClient();
  const user = useQuery(shellBootstrapQuery(services)).data?.user;
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [qr, setQr] = useState<string | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const rateLimit = useAuthRateLimit();
  const [pending, setPending] = useState(false);
  const sensitive = async (action: () => Promise<void>) => {
    if (pending || rateLimit.blocked) return;
    try {
      if (!password) {
        setNotice("Введите текущий пароль для этого действия.");
        return;
      }
      setPending(true);
      await services.identity.confirmPassword(password);
      await action();
      setPassword("");
      setPending(false);
      await client.invalidateQueries({ queryKey: shellBootstrapKey });
    } catch (error) {
      setPending(false);
      rateLimit.handleError(error);
      setNotice(errorMessage(error));
    }
  };
  const setup = () =>
    void sensitive(async () => {
      await services.identity.setupTwoFactor();
      const data = await services.identity.twoFactorQrCode();
      setQr(data.svg);
    });
  const confirm = async () => {
    try {
      await services.identity.confirmTwoFactor(otp);
      setQr(null);
      setOtp("");
      setCodes(await services.identity.recoveryCodes());
      await client.invalidateQueries({ queryKey: shellBootstrapKey });
    } catch (error) {
      setNotice(errorMessage(error));
    }
  };
  if (!user) return null;
  return (
    <section className="content-panel">
      <div className="panel-heading">
        <div>
          <p className="eyebrow">Аккаунт</p>
          <h1>Безопасность</h1>
        </div>
      </div>
      {notice && <Notice>{notice}</Notice>}
      <section className="auth-settings-section">
        <h2>Email</h2>
        <p>
          {user.email_verified_at
            ? "Email подтверждён."
            : "Email пока не подтверждён."}
        </p>
        {!user.email_verified_at && (
          <button
            type="button"
            className="button button--secondary"
            onClick={() =>
              void services.identity
                .sendEmailVerification()
                .then(() => setNotice("Письмо с подтверждением отправлено."))
                .catch((error: unknown) => setNotice(errorMessage(error)))
            }
          >
            Отправить письмо повторно
          </button>
        )}
      </section>
      <section className="auth-settings-section">
        <h2>Смена пароля</h2>
        <p>Используйте уникальный пароль длиной не менее 8 символов.</p>
        <ChangePasswordForm services={services} onSuccess={setNotice} />
      </section>
      <section className="auth-settings-section">
        <h2>Двухфакторная аутентификация</h2>
        <p>
          {user.two_factor.enabled
            ? "Защита включена."
            : "Дополнительная защита пока отключена."}
        </p>
        <LabeledInput
          label="Текущий пароль для чувствительных действий"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={setPassword}
        />
        {!user.two_factor.enabled ? (
          <>
            <button
              type="button"
              className="button button--secondary"
              onClick={setup}
              disabled={pending || rateLimit.blocked}
            >
              Настроить 2FA
            </button>
            {qr && (
              <div className="two-factor-setup">
                <img
                  src={`data:image/svg+xml;utf8,${encodeURIComponent(qr)}`}
                  alt="QR-код для приложения-аутентификатора"
                />
                <LabeledInput
                  label="Код из приложения"
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  value={otp}
                  onChange={setOtp}
                />
                <button
                  type="button"
                  className="button button--primary"
                  disabled={!otp.trim()}
                  onClick={() => void confirm()}
                >
                  Подтвердить 2FA
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            <button
              type="button"
              className="button button--secondary"
              onClick={() =>
                void sensitive(async () =>
                  setCodes(await services.identity.recoveryCodes()),
                )
              }
              disabled={pending || rateLimit.blocked}
            >
              Показать recovery-коды
            </button>
            <button
              type="button"
              className="button button--secondary"
              onClick={() =>
                void sensitive(async () =>
                  setCodes(await services.identity.regenerateRecoveryCodes()),
                )
              }
              disabled={pending || rateLimit.blocked}
            >
              Сгенерировать новые recovery-коды
            </button>
            <button
              type="button"
              className="button button--text"
              onClick={() =>
                void sensitive(() => services.identity.disableTwoFactor())
              }
              disabled={pending || rateLimit.blocked}
            >
              Отключить 2FA
            </button>
          </>
        )}
        {codes && (
          <div
            className="recovery-codes"
            role="region"
            aria-label="Recovery-коды"
          >
            <p>
              Сохраните коды сейчас: они не сохраняются в браузере и исчезнут
              после закрытия блока.
            </p>
            <ul>
              {codes.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <button
              type="button"
              className="button button--text"
              onClick={() => setCodes(null)}
            >
              Скрыть коды
            </button>
          </div>
        )}
      </section>
    </section>
  );
}

export function SessionsPage({ services }: { services: ShellServices }) {
  const client = useQueryClient();
  const [notice, setNotice] = useState<string | null>(null);
  const sessions = useQuery({
    queryKey: ["auth", "sessions"],
    queryFn: () => services.identity.sessions(),
  });
  const refreshSessions = () =>
    client.invalidateQueries({ queryKey: ["auth", "sessions"] });
  const revoke = useMutation({
    mutationFn: (id: string) => services.identity.destroySession(id),
    onSuccess: async () => {
      setNotice("Сеанс завершён.");
      await refreshSessions();
    },
    onError: (error) => setNotice(errorMessage(error)),
  });
  const others = useMutation({
    mutationFn: () => services.identity.destroyOtherSessions(),
    onSuccess: async ({ destroyed }) => {
      setNotice(
        destroyed
          ? `Завершено сеансов: ${destroyed}.`
          : "Других активных сеансов нет.",
      );
      await refreshSessions();
    },
    onError: (error) => setNotice(errorMessage(error)),
  });
  const hasOtherSessions =
    sessions.data?.some((session) => !session.current) ?? false;
  const isMutating = revoke.isPending || others.isPending;

  return (
    <section className="content-panel">
      <div className="panel-heading">
        <div>
          <p className="eyebrow">Аккаунт</p>
          <h1>Активные сессии</h1>
          <p>
            Завершайте сеансы на устройствах, которыми больше не пользуетесь.
          </p>
        </div>
        <button
          type="button"
          className="button button--secondary"
          disabled={!hasOtherSessions || isMutating}
          onClick={() => {
            setNotice(null);
            others.mutate();
          }}
        >
          Завершить остальные
        </button>
      </div>
      {notice && <Notice role="status">{notice}</Notice>}
      {sessions.isPending && (
        <Notice role="status">Загружаем активные сессии…</Notice>
      )}
      {sessions.isError && (
        <Notice>
          <p>Не удалось загрузить сессии.</p>
          <button
            type="button"
            className="button button--text"
            onClick={() => void sessions.refetch()}
          >
            Повторить
          </button>
        </Notice>
      )}
      {sessions.data && sessions.data.length === 0 && (
        <p>Активных сессий не найдено.</p>
      )}
      {sessions.data && sessions.data.length > 0 && (
        <ul className="session-list" aria-label="Активные сессии">
          {sessions.data.map((session) => (
            <li key={session.id}>
              <div>
                <strong>
                  {session.current ? "Текущая сессия" : "Сеанс устройства"}
                </strong>
                <span>
                  {session.user_agent} · {session.ip_address ?? "IP скрыт"}
                </span>
                <span>
                  Последняя активность:{" "}
                  {new Intl.DateTimeFormat("ru-RU", {
                    dateStyle: "medium",
                    timeStyle: "short",
                  }).format(new Date(session.last_activity_at))}
                </span>
              </div>
              {!session.current && (
                <button
                  type="button"
                  className="button button--text"
                  disabled={isMutating}
                  onClick={() => {
                    setNotice(null);
                    revoke.mutate(session.id);
                  }}
                >
                  Завершить<span className="sr-only"> сеанс устройства</span>
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
